import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi } from '@/services/api';
import { X, AlertTriangle, Loader2, Trash2, Eye, EyeOff, Check } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { getApiErrorMessage } from '@/utils/apiError';
import toast from 'react-hot-toast';

interface Props {
  onClose: () => void;
}

export default function ClearDataModal({ onClose }: Props) {
  useEscapeToClose(onClose);
  const [password, setPassword] = useState('');
  const [confirmationWord, setConfirmationWord] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const qc = useQueryClient();

  const isConfirmed = confirmationWord.trim().toUpperCase() === 'DELETE';

  const clearDataMutation = useMutation({
    mutationFn: (pwd: string) => posApi.clearSalesData(pwd),
    onSuccess: () => {
      toast.success('All sales data cleared successfully!');
      qc.invalidateQueries();
      onClose();
    },
    onError: (err: any) => {
      setError(getApiErrorMessage(err, 'Failed to clear sales data. Please check your password.'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isConfirmed) {
      setError('Please type DELETE in the confirmation box (do not enter your email or username).');
      return;
    }
    if (!password.trim()) {
      setError('Password is required.');
      return;
    }
    setError('');
    clearDataMutation.mutate(password);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h3 className="font-semibold text-red-900 text-lg">Reset All Sales</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-red-400 hover:text-red-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5" autoComplete="off">
          {/* Hidden dummy username field to absorb browser credential autofill and prevent it from hijacking the DELETE confirmation box */}
          <div
            style={{ position: 'absolute', opacity: 0, height: 0, width: 0, zIndex: -1, overflow: 'hidden' }}
            aria-hidden="true"
          >
            <input
              type="text"
              name="username"
              autoComplete="username"
              tabIndex={-1}
              readOnly
              aria-hidden="true"
              defaultValue=""
            />
          </div>

          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-900">
            <strong>Full company wipe</strong> — ignores any table selection.
            For checked rows only, cancel and use <strong>Reset Selected Sales</strong>.
          </div>
          <p className="text-sm text-gray-600 mb-4">
            This action is <strong>irreversible</strong>. It will permanently delete:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 mb-6 space-y-2">
            <li>Every POS Invoice (all sales)</li>
            <li>Every POS Sales Return</li>
            <li>All POS Shifts and Variances</li>
            <li>All Cash Ledgers</li>
            <li>Corresponding stock movements (inventory restored)</li>
          </ul>

          <div className="space-y-4">
            <div>
              <label htmlFor="confirm-delete-input" className="block text-sm font-medium text-gray-700 mb-1">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                id="confirm-delete-input"
                name="confirm_delete_word"
                type="text"
                autoComplete="off"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                placeholder="DELETE"
                value={confirmationWord}
                onChange={(e) => {
                  setConfirmationWord(e.target.value);
                  if (error) setError('');
                }}
                className={`w-full text-sm border rounded-lg px-3 py-2 outline-none transition-all ${
                  isConfirmed
                    ? 'border-emerald-400 bg-emerald-50/30 text-emerald-900 focus:ring-2 focus:ring-emerald-500'
                    : confirmationWord.length > 0
                    ? 'border-amber-400 bg-amber-50/30 text-gray-900 focus:ring-2 focus:ring-amber-500'
                    : 'border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500'
                }`}
              />

              {/* Helper feedback when autofilled or mistyped */}
              {confirmationWord.length > 0 && !isConfirmed && (
                <div className="mt-1.5 flex items-center justify-between text-xs text-amber-800 bg-amber-50 p-2 rounded-lg border border-amber-200">
                  <span>Type <strong>DELETE</strong> here (not your email or username).</span>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmationWord('DELETE');
                      setError('');
                    }}
                    className="font-semibold text-rose-700 hover:text-rose-900 underline ml-2 shrink-0 cursor-pointer"
                  >
                    Set "DELETE"
                  </button>
                </div>
              )}

              {isConfirmed && (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-emerald-700 font-medium">
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Confirmation matched</span>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="clear-data-password" className="block text-sm font-medium text-gray-700 mb-1">
                Enter your password
              </label>
              <div className="relative">
                <input
                  id="clear-data-password"
                  name="password"
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={clearDataMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={clearDataMutation.isPending}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 cursor-pointer ${
                isConfirmed && password
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                  : 'bg-red-500 hover:bg-red-600 opacity-90'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {clearDataMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              {clearDataMutation.isPending ? 'Resetting all…' : 'Reset All Sales'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
